<script>
import {
    BBadge,
    BCard,
    BCardText,
    BCardTitle,
    BFormInput,
    BFormRadio,
} from 'bootstrap-vue'
import TextEllipsis from '@core/components/common/TextEllipsis.vue'

export default {
    components: {
        BBadge,
        BCard,
        BCardText,
        BCardTitle,
        BFormInput,
        BFormRadio,
        TextEllipsis,
    },

    props: {
        groups: {
            type: Array,
            default() {
                return []
            },
        },
        legislations: {
            type: Array,
            default() {
                return []
            },
        },
        answers: {
            type: Array,
            default() {
                return []
            },
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            answersByQuestionId: {},
        }
    },

    watch: {
        answers: {
            immediate: true,
            handler(answers) {
                this.syncAnswers(answers || [])
            },
        },
    },

    methods: {
        syncAnswers(answers) {
            const next = {}

            answers.forEach(answer => {
                if (!answer || answer.questionId === undefined || answer.questionId === null) {
                    return
                }

                next[answer.questionId] = {
                    questionId: answer.questionId,
                    answer: answer.answer === undefined ? null : answer.answer,
                    comment: answer.comment || null,
                }
            })

            this.answersByQuestionId = next
        },

        legislationTitle(id) {
            const legislation = this.legislations.find(item => String(item.id) === String(id))

            return legislation ? legislation.title : id
        },

        legislationIdsFor(item) {
            return Array.isArray(item.legislationIds) ? item.legislationIds : []
        },

        isBooleanQuestion(question) {
            return question.type === 'boolean'
        },

        answerStateForQuestion(question) {
            if (Object.prototype.hasOwnProperty.call(this.answersByQuestionId, question.id)) {
                return this.answersByQuestionId[question.id]
            }

            return {
                questionId: question.id,
                answer: null,
                comment: null,
            }
        },

        answerForQuestion(question) {
            return this.answerStateForQuestion(question).answer
        },

        commentForQuestion(question) {
            return this.answerStateForQuestion(question).comment || ''
        },

        answerPayload() {
            return Object.values(this.answersByQuestionId)
        },

        updateAnswer(question, patch) {
            if (this.readOnly) {
                return
            }

            const current = this.answerStateForQuestion(question)
            const next = {
                ...current,
                ...patch,
            }

            if (Object.prototype.hasOwnProperty.call(patch, 'comment')) {
                next.comment = patch.comment === '' ? null : patch.comment
            }

            this.$set(this.answersByQuestionId, question.id, next)
            this.$emit('answerChange', {
                ...next,
            })
            this.$emit('answersChange', this.answerPayload())
        },

        setAnswer(question, answer) {
            this.updateAnswer(question, { answer })
        },

        setComment(question, comment) {
            this.updateAnswer(question, { comment })
        },
    },
}
</script>

<template>
    <div class="regulation-questions">
        <b-card
            v-for="group in groups"
            :key="group.id"
            no-body
            border-variant="light"
            class="mb-1"
        >
            <div class="p-1">
                <div class="d-flex align-items-start justify-content-between">
                    <div>
                        <b-card-title class="mb-50">
                            {{ group.title }}
                        </b-card-title>
                        <b-card-text
                            v-if="group.description"
                            class="text-muted mb-0"
                        >
                            {{ group.description }}
                        </b-card-text>
                    </div>
                    <div class="regulation-questions__legislations">
                        <b-badge
                            v-for="legislationId in legislationIdsFor(group)"
                            :key="legislationId"
                            variant="light-secondary"
                            class="ml-50"
                        >
                            {{ legislationTitle(legislationId) }}
                        </b-badge>
                    </div>
                </div>
            </div>

            <div class="regulation-questions__list">
                <div
                    v-for="question in group.questions"
                    :key="question.id"
                    class="regulation-questions__item"
                >
                    <div class="regulation-questions__question-row">
                        <div class="regulation-questions__question">
                            {{ question.question }}
                        </div>
                        <div
                            v-if="isBooleanQuestion(question)"
                            class="regulation-questions__answer"
                        >
                            <div class="demo-inline-spacing regulation-questions__radio">
                                <b-form-radio
                                    class="mt-0"
                                    :name="`regulation-question-${question.id}`"
                                    :value="true"
                                    :checked="answerForQuestion(question)"
                                    :disabled="readOnly"
                                    @change="setAnswer(question, true)"
                                >
                                    {{ $t('Component(Choice):yes') }}
                                </b-form-radio>
                                <b-form-radio
                                    class="mt-0"
                                    :name="`regulation-question-${question.id}`"
                                    :value="false"
                                    :checked="answerForQuestion(question)"
                                    :disabled="readOnly"
                                    @change="setAnswer(question, false)"
                                >
                                    {{ $t('Component(Choice):no') }}
                                </b-form-radio>
                            </div>
                            <TextEllipsis
                                v-if="readOnly"
                                class="regulation-questions__comment"
                                :value="commentForQuestion(question)"
                            />
                            <b-form-input
                                v-else
                                class="regulation-questions__comment"
                                :value="commentForQuestion(question)"
                                :placeholder="$t('Component(Label):comment')"
                                size="sm"
                                @input="(comment) => setComment(question, comment)"
                            />
                        </div>
                    </div>
                    <div class="regulation-questions__legislations mt-50">
                        <b-badge
                            v-for="legislationId in legislationIdsFor(question)"
                            :key="legislationId"
                            variant="light-secondary"
                            class="mr-50"
                        >
                            {{ legislationTitle(legislationId) }}
                        </b-badge>
                    </div>
                    <div
                        v-if="question.description"
                        class="text-muted mt-50"
                    >
                        {{ question.description }}
                    </div>
                </div>
            </div>
        </b-card>
    </div>
</template>

<style scoped>
.regulation-questions__list {
    border-top: 1px solid #ebe9f1;
}

.regulation-questions__item {
    padding: .85rem 1rem;
}

.regulation-questions__item + .regulation-questions__item {
    border-top: 1px solid #ebe9f1;
}

.regulation-questions__question {
    font-weight: 500;
}

.regulation-questions__question-row {
    align-items: flex-start;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
}

.regulation-questions__answer {
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    gap: 1rem;
}

.regulation-questions__radio {
    flex: 0 0 auto;
}

.regulation-questions__comment {
    min-width: 220px;
    width: 260px;
}

.regulation-questions__legislations {
    flex: 0 0 auto;
}

@media (max-width: 768px) {
    .regulation-questions__question-row {
        flex-direction: column;
    }

    .regulation-questions__answer {
        align-items: flex-start;
        flex-wrap: wrap;
        width: 100%;
    }

    .regulation-questions__comment {
        min-width: 0;
        width: 100%;
    }
}
</style>
